import { Loader, Radio, Typography } from '@recruit-robin/robin-theme/web-components';
import { AnimatePresence } from 'motion/react';

import { useDictionary } from '@/shared/lib/i18n';
import { MarkerPin_01, PhoneCall_01, VideoRecorder } from '@recruit-robin/robin-theme/web-icons';
import { motion } from 'motion/react';
import { InterviewType, SchedulerConfigListViewModel } from '../../../model';
import { FadeIn } from '../FadeIn';
import { CallInterview, InPersonInterview, OnlineInterview } from '../forms';
import { InterviewError, InterviewValue } from '../InterviewTypeSelection.types';
import { useStyles } from './Body.styles';

type BodyProps = {
  isLoadingInitialData?: boolean;
  selectedInterviewType: InterviewType | null;
  onSelectedInterviewType: (value: InterviewType) => void;
  schedulerConfig?: SchedulerConfigListViewModel;
  error?: InterviewError;
  interviewValue?: InterviewValue;
  setInterviewValue?: React.Dispatch<React.SetStateAction<InterviewValue>>;
  showOnlineOption?: boolean;
};

const MeetingDuration = ({ duration, isSelected }: { duration: number; isSelected: boolean }) => {
  const classes = useStyles();

  return (
    <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <Typography
        customCss={[classes.meetingDuration, isSelected && classes.meetingDurationSelected]}
        variant={(v) => v.supporting.label}
        color={(c) => c.primary.fuchsia[50]}
        inline>
        {duration}min
      </Typography>
    </motion.span>
  );
};

export const Body = ({
  isLoadingInitialData = false,
  selectedInterviewType,
  onSelectedInterviewType,
  error,
  schedulerConfig,
  interviewValue: value,
  setInterviewValue: setValue,
  showOnlineOption = true,
}: BodyProps) => {
  const dictionary = useDictionary();
  const classes = useStyles();

  return (
    <div css={classes.body}>
      <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[90]}>
        {dictionary.whatTypeOfInterviewWouldYouLikeToSchedule}
      </Typography>

      <div css={classes.radioGroup}>
        <Radio.Root onChange={onSelectedInterviewType} value={selectedInterviewType ?? undefined}>
          {showOnlineOption && (
            <Radio.Item
              customCss={[
                classes.radioItem,
                selectedInterviewType === InterviewType.Online && classes.radioItemSelected,
              ]}
              value={InterviewType.Online}>
              <VideoRecorder size={26} />

              <Typography variant={(v) => v.supporting.label} inline>
                {dictionary.videoInterviewAboutTheVacancy}
              </Typography>

              <AnimatePresence>
                {schedulerConfig?.[InterviewType.Online].duration && (
                  <MeetingDuration
                    duration={schedulerConfig?.[InterviewType.Online].duration}
                    isSelected={selectedInterviewType === InterviewType.Online}
                  />
                )}
              </AnimatePresence>
            </Radio.Item>
          )}

          <Radio.Item
            customCss={[
              classes.radioItem,
              selectedInterviewType === InterviewType.InPerson && classes.radioItemSelected,
            ]}
            value={InterviewType.InPerson}>
            <MarkerPin_01 size={26} />

            <Typography variant={(v) => v.supporting.label} inline>
              {dictionary.introductionAtTheLocation}
            </Typography>

            <AnimatePresence>
              {schedulerConfig?.[InterviewType.InPerson].duration && (
                <MeetingDuration
                  duration={schedulerConfig?.[InterviewType.InPerson].duration}
                  isSelected={selectedInterviewType === InterviewType.InPerson}
                />
              )}
            </AnimatePresence>
          </Radio.Item>

          <Radio.Item
            customCss={[classes.radioItem, selectedInterviewType === InterviewType.Call && classes.radioItemSelected]}
            value={InterviewType.Call}>
            <PhoneCall_01 size={24} />

            <Typography variant={(v) => v.supporting.label} inline>
              {dictionary.telephoneConversationAboutTheVacancy}
            </Typography>

            <AnimatePresence>
              {schedulerConfig?.[InterviewType.Call].duration && (
                <MeetingDuration
                  duration={schedulerConfig?.[InterviewType.Call].duration}
                  isSelected={selectedInterviewType === InterviewType.Call}
                />
              )}
            </AnimatePresence>
          </Radio.Item>
        </Radio.Root>
      </div>

      <div css={classes.radioSelectionResult}>
        <AnimatePresence initial={false} mode="wait">
          {!selectedInterviewType && (
            <FadeIn>
              <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[90]}>
                {dictionary.selectAnOption}
              </Typography>
            </FadeIn>
          )}

          {isLoadingInitialData && <Loader key="loader" customCss={classes.loader} size={24} variant="ring-4" />}

          {value && setValue && (
            <>
              {!isLoadingInitialData && selectedInterviewType === InterviewType.Online && (
                <OnlineInterview key={InterviewType.Online} />
              )}
              {!isLoadingInitialData && selectedInterviewType === InterviewType.InPerson && (
                <InPersonInterview
                  key={InterviewType.InPerson}
                  value={value}
                  onChange={setValue}
                  error={error ?? undefined}
                />
              )}
              {!isLoadingInitialData && selectedInterviewType === InterviewType.Call && (
                <CallInterview key={InterviewType.Call} value={value} onChange={setValue} error={error ?? undefined} />
              )}
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
